// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CustomErrorClass, ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { Rectangle } from '@shared/commons/Geometry';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
import { LinkedSystem } from '../misc/LinkedSystem';
import RiskCalculatorTemplate from '@front/templates/RiskCalculatorTemplate.html';
import OrderEditControlsTemplate from '@front/templates/OrderEditControlsTemplate.html';
import { DockSystemInstance } from '../DockSystem';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { OrderEditViewBase } from './OrderEditViewBase';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { PlacedFrom } from '@shared/utils/Trading/PlacedFrom';
import { DynProperty } from '@shared/commons/DynProperty';
import { Instrument } from '@shared/commons/cache/Instrument';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { OrderEditUpdateData } from '@shared/utils/Trading/OrderEditUpdateData';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { TradingNumericErrorChecker } from '@shared/commons/Trading/TradingNumericErrorChecker';
import { OrderExecutorUtils } from '@shared/commons/Trading/OrderExecutorUtils';
import { WDSettings } from '../settings/WDGeneralSettings';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { ControlsUtils } from '../UtilsClasses/ControlsUtils';
export class RiskCalculator extends OrderEditViewBase {
    constructor() {
        super(...arguments);
        this.headerLocaleKey = 'panel.riskCalculator';
        this.NewParametrsHandler = null;
        this.closeTimeout = null;
        this.AutoHideHeaderUpdateHandler = null;
        this.IsResized = false;
    }
    getType() {
        return PanelNames.RiskCalculator;
    }
    oninit() {
        super.oninit();
        this.observe('instrument', this.onInstrumentChanged);
        this.observe('account', this.onAccountChanged);
        void this.set('singleAccount', DataCache.getNumberOfAccounts() === 1);
        this.observe('orderType', this.onOrderTypeChanged);
        this.observe('instrument account quantity side tif productType', this.onTradingDataChanged);
        this.on('placeOrderClick', this.onPlaceOrderClick);
        this.on('btnCancelClick', () => {
            this.close();
        });
        // if (!Resources.isHidden('OrderEntry.InfoBlock.Visibility'))
        // {
        void this.set('marginOEAllowed', true);
        this.on('onMarginOEVisibleStateChanged', this.onMarginOEVisibleStateChanged);
        // }
        // this.observe('delayedVisible snapshotVisible dataSourceVisible', this.deferLayout)
        // this.observe('dataSourceOpen', this.onDataSourceOpen)
        DataCache.OnUpdateAccount.Subscribe(this.onUpdateAccount, this);
    }
    oncomplete() {
        super.oncomplete();
        void this.set({ isAllowAttachDetachPanel: DockSystemInstance.isAutoHideAvaliable() });
        DockSystemInstance.RegisterPanel(this);
        if (this.get('isAlert')) {
            void this.set({
                isAllowAttachDetachPanel: false,
                isAccountLinkShow: false,
                isSymbolLinkShow: false,
                canLinkByAccount: false
            });
        }
        if (this.get('attached')) {
            void this.set({ visible: false, movable: false });
            DockSystemInstance.addToAutoHide(this);
            MainWindowManager.MainWindow.resetFocus();
        }
        this.SetFirstElement();
        this.center();
    }
    updatePanelHeader() {
        const ins = this.get('instrument');
        const header = Resources.getResource(this.headerLocaleKey) + (!isNullOrUndefined(ins) ? ' ' + ins.DisplayName() : '');
        void this.set({ header });
        if (!isNullOrUndefined(this.AutoHideHeaderUpdateHandler)) {
            this.AutoHideHeaderUpdateHandler(header);
        }
    }
    onInstrumentChanged(instrument, lastInstrument) {
        if (isNullOrUndefined(instrument) || instrument === lastInstrument) {
            return;
        }
        this.subscribeRiskSettingsUpdate(instrument, lastInstrument);
        this.instrumentSettingsUpdate();
        this.symbolLink_Out(false, instrument);
        this.updatePanelHeader();
        this.deferLayout();
    }
    onAccountChanged(account, lastAccount) {
        if (isNullOrUndefined(account)) {
            return;
        }
        this.accountLink_Out(false, account);
    }
    UpdateAutoHideSate(state) {
        if (this.get('attached') || this.get('isAlert')) {
            return;
        }
        void this.set('isAllowAttachDetachPanel', state);
    }
    ForcedAutoHide() {
        if (!this.get('attached')) {
            return;
        }
        clearTimeout(this.closeTimeout);
        void this.set({ visible: false });
        MainWindowManager.MainWindow.resetFocus();
    }
    AttachClick(_event) {
        let attached = this.get('attached');
        if (attached) {
            attached = !attached;
            clearTimeout(this.closeTimeout);
            void this.set({ visible: true, movable: true, attached });
            DockSystemInstance.removeFromAutoHide(this);
        }
        else {
            attached = !attached;
            this.IsResized = false;
            void this.set({ visible: false, movable: false, attached });
            DockSystemInstance.addToAutoHide(this);
        }
        this.layout();
        if (!attached) {
            this.center();
        }
        MainWindowManager.MainWindow.resetFocus();
        if (this.get('marginOEVisible') && !isNullOrUndefined(this.Controls.marginOEControl)) {
            this.Controls.marginOEControl.layoutTable();
        }
        const btnattachText = attached
            ? Resources.getResource('panel.external.Dettach')
            : Resources.getResource('panel.external.Attach');
        void this.set('attachDetachPanelText', btnattachText);
    }
    AutoHideHover() {
        clearTimeout(this.closeTimeout);
        const visible = this.get('visible');
        if (visible) {
            return;
        }
        void this.set({ visible: true });
        this.ChangeAutohideLocation();
    }
    AutoHideLeave() {
        if (this.get('attached')) {
            this.MakeMeHidden();
        }
    }
    MakeMeHidden() {
        clearTimeout(this.closeTimeout);
        this.closeTimeout = setTimeout(() => {
            if (!this.get('focused')) {
                void this.set({ visible: false });
            }
            clearTimeout(this.closeTimeout);
        }, RiskCalculator.TIME_TO_HIDE);
    }
    ChangeAutohideLocation() {
        const newCoord = ControlsUtils.CorrectPopupLocation(new Rectangle(2000, 52, this.get('width'), this.get('height')));
        this.setLocation(newCoord.newX - 27, newCoord.newY);
    }
    lostFocus() {
        var _a, _b;
        super.lostFocus();
        if (!this.get('attached')) {
            return;
        }
        if ((_a = MainWindowManager.TerceraInstrumentLookupDropDownForm) === null || _a === void 0 ? void 0 : _a.get('visible')) {
            MainWindowManager.TerceraInstrumentLookupDropDownForm.AfterLostFocusWaiting = this;
            return;
        }
        if ((_b = MainWindowManager.TerceraAccountLookupDropDownForm) === null || _b === void 0 ? void 0 : _b.get('visible')) {
            MainWindowManager.TerceraAccountLookupDropDownForm.AfterLostFocusWaiting = this;
            return;
        }
        this.MakeMeHidden();
    }
    onMouseEnter(event) {
        this.AutoHideHover();
    }
    onMouseLeave(event) {
        this.AutoHideLeave();
    }
    gotFocus() {
        super.gotFocus();
        clearTimeout(this.closeTimeout);
    }
    dispose() {
        if (this.get('attached')) {
            DockSystemInstance.removeFromAutoHide(this);
        }
        DockSystemInstance.UnRegisterPanel(this);
        DataCache.OnUpdateAccount.UnSubscribe(this.onUpdateAccount, this);
        const instrument = this.get('instrument');
        if (!isNullOrUndefined(instrument)) {
            instrument.RiskSettingsUpdated.UnSubscribe(this.updateTradingAllowedStuff, this);
        }
        super.dispose();
    }
    SetFirstElement() {
        if (this.get('singleAccount')) {
            this.Controls.OEInstrumentLookup.setFocus();
        }
        else {
            this.Controls.OEAccountLookup.setFocus();
        }
    }
    onPlaceOrderClick() {
        if (this.get('isAlert')) {
            this.saveOrderParam();
        }
        else {
            if (TradingNumericErrorChecker.HasErrors(this)) {
                return;
            }
            this.placeOrder(this.orderEdit);
        }
    }
    selectOrderType(newOrderType) {
        var _a;
        const orderTypeDict = OrderExecutorUtils.getAllowedOrderTypeDict(this.get('account'), this.get('instrument'), [], WDSettings.orderType, false);
        const orderType = (_a = orderTypeDict[newOrderType]) !== null && _a !== void 0 ? _a : orderTypeDict[OrderType.Market];
        const selectedItem = { value: orderType, text: Resources.getResource(orderType.localizationKey()) };
        void this.set('selOrderTypeCBItem', selectedItem);
    }
    onOrderTypeChanged(newOrderType, oldOrderType) {
        if (isNullOrUndefined(newOrderType) || newOrderType === oldOrderType) {
            return;
        }
        const orderEdit = newOrderType.createOrderEditObject({
            dataCache: DataCache
        });
        this.setOrderEdit(orderEdit);
        orderEdit.updateParameters(new OrderEditUpdateData(null, this.getAllTradingDataDict()));
    }
    onTradingDataChanged(newVal, oldVal, key) {
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit))
            return;
        const tradingDataDict = {};
        tradingDataDict[key] = newVal;
        orderEdit.updateParameters(new OrderEditUpdateData(null, tradingDataDict));
        this.deferLayout();
    }
    getAllTradingDataDict() {
        const tradingDataDict = {};
        const instrument = this.get('instrument');
        if (!isNullOrUndefined(instrument)) {
            tradingDataDict.instrument = instrument;
        }
        const account = this.get('account');
        if (!isNullOrUndefined(account)) {
            tradingDataDict.account = account;
        }
        const quantity = this.get('quantity');
        if (!isNullOrUndefined(quantity)) {
            tradingDataDict.quantity = quantity;
        }
        const tif = this.get('tif');
        if (!isNullOrUndefined(tif)) {
            tradingDataDict.tif = tif;
        }
        const side = this.get('side');
        if (!isNullOrUndefined(side)) {
            tradingDataDict.side = side;
        }
        const productType = this.get('productType');
        if (!isNullOrUndefined(productType)) {
            tradingDataDict.productType = productType;
        }
        return tradingDataDict;
    }
    layout() {
        this.updateOrderEditParametersVisibility();
        if (this.get('attached')) {
            void this.set('height', MainWindowManager.MainWindow.GetAvaliableHeight());
        }
        else {
            void this.set('height', null);
        }
        if (this.get('marginOEVisible') && !isNullOrUndefined(this.Controls.marginOEControl)) {
            this.Controls.marginOEControl.layoutTable();
        }
    }
    saveOrderParam() {
        if (!isNullOrUndefined(this.NewParametrsHandler)) {
            this.NewParametrsHandler(this.orderEdit);
        }
    }
    placeOrder(orderEdit) {
        orderEdit.placedFrom = PlacedFrom.WEB_OE;
        DataCache.FOrderExecutor.placeOrderPromise(orderEdit).catch(function () {
            const ex = new CustomErrorClass('RiskCalculator error', 'RiskCalculator.placeOrder', 'placeOrder -> placeOrderPromise');
            ErrorInformationStorage.GetException(ex);
        });
    }
    updateSettings() {
        const ins = this.get('instrument');
        const acc = this.get('account');
        if (isNullOrUndefined(ins) || isNullOrUndefined(acc)) {
            return;
        }
        this.updateTradingAllowedAndReasonTooltip();
    }
    callBack(properties) {
        super.callBack(properties);
        void this.set('instrument', this.getCallBackInstrument(properties, 'symbol'));
        let dp = DynProperty.getPropertyByName(properties, 'account');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            void this.set('account', SessionSettings.getDefValueFromObj(DataCache.Accounts[dp.value], DataCache.Accounts));
        }
        dp = DynProperty.getPropertyByName(properties, 'marginOEVisible');
        if (!isNullOrUndefined(dp)) {
            void this.set('marginOEVisible', dp.value);
        }
        // dp = DynProperty.getPropertyByName(properties, 'dataSourceOpen');
        // if (dp) this.set('dataSourceOpen', dp.value);
        const attached = DynProperty.getPropValue(properties, 'attached');
        void this.set({ attached });
    }
    Properties() {
        const properties = super.Properties();
        const ins = this.get('instrument');
        if (!isNullOrUndefined(ins)) {
            properties.push(new DynProperty('symbol', ins.GetInteriorID(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        const acc = this.get('account');
        if (!isNullOrUndefined(acc)) {
            properties.push(new DynProperty('account', acc.AcctNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));
        }
        const marginOEVisible = this.get('marginOEVisible');
        properties.push(new DynProperty('marginOEVisible', marginOEVisible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        // var dataSourceOpen = this.get('dataSourceOpen');
        // properties.push(new DynProperty("dataSourceOpen", dataSourceOpen, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        properties.push(new DynProperty('attached', this.get('attached'), DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));
        return properties;
    }
    // #endregion
    // #region Link
    symbolLink_Out(newSubscriber, instrument) {
        if (isNullOrUndefined(instrument)) {
            const ins = this.get('instrument');
            if (isNullOrUndefined(ins))
                return;
            instrument = ins;
        }
        const color = this.get('symbolLinkValue');
        if (color !== TerceraLinkControlConstants.STATE_NONE) {
            LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber);
        }
    }
    symbolLink_In(symbolName) {
        const newInstr = DataCache.getInstrumentByName(symbolName);
        if (!isNullOrUndefined(newInstr)) {
            void this.set('instrument', newInstr);
        }
    }
    // #endregion Link
    localize() {
        super.localize();
        void this.set('buttonCancelText', Resources.getResource('general.messageBox.cancel'));
    }
    onUpdateAccount(account) {
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit))
            return;
        orderEdit.onUpdateAccount(account);
    }
    instrumentSettingsUpdate() {
        const instrument = this.get('instrument');
        const account = this.get('account');
        if (!Instrument.IsWorkingInstrument(instrument)) {
            return;
        }
        if (!isNullOrUndefined(account)) {
            this.updateTradingAllowedAndReasonTooltip();
        }
        void this.set('productTypeShow', instrument.isProductTypeVisible());
    }
    updateTradingAllowedAndReasonTooltip() {
        const instrument = this.get('instrument');
        const account = this.get('account');
        const ordertype = this.get('orderType');
        let ordT = -1;
        if (!isNullOrUndefined(ordertype)) {
            ordT = ordertype.id();
        }
        const tradingAllowed = IsAllowed.IsTradingAllowed(account, instrument, ordT);
        let tradingForbiddenReason = tradingAllowed.Allowed ? '' : tradingAllowed.ReasonText;
        if (tradingAllowed.Allowed && ordT === -1) {
            tradingForbiddenReason = Resources.getResource('Not selected order type');
            tradingAllowed.Allowed = false;
        }
        void this.set({
            isAllowedResponce: tradingAllowed,
            tradingAllowed: tradingAllowed.Allowed,
            tradingForbiddenReason
        });
    }
    subscribeRiskSettingsUpdate(instrument, lastInstrument) {
        if (!isNullOrUndefined(lastInstrument)) {
            lastInstrument.RiskSettingsUpdated.UnSubscribe(this.instrumentSettingsUpdate, this);
        }
        if (!isNullOrUndefined(instrument)) {
            instrument.RiskSettingsUpdated.Subscribe(this.instrumentSettingsUpdate, this);
        }
    }
    onMarginOEVisibleStateChanged() {
        const oldState = this.get('marginOEVisible');
        const newState = !oldState;
        void this.set('marginOEVisible', newState);
        void this.set('width', newState ? 685 : 350);
        if (!this.get('attached')) {
            void this.set('resizable', newState);
        }
        if (!newState) {
            this.IsResized = false;
            this.layout();
        }
        if (this.get('attached')) {
            this.ChangeAutohideLocation();
        }
    }
    onQuantityChanged(qtyValue) {
        const orderEdit = this.orderEdit;
        if (isNullOrUndefined(orderEdit))
            return;
        const ins = orderEdit.instrument;
        if (isNullOrUndefined(ins))
            return;
        const OEQty = !isNullOrUndefined(this.Controls) ? this.Controls.OEQuantity : null;
        const acc = orderEdit.account;
        const pt = orderEdit.productType;
        const isAllowedResponce = this.get('isAllowedResponce');
        if (isAllowedResponce === null || isAllowedResponce === void 0 ? void 0 : isAllowedResponce.Allowed) {
            // доп валидацию по qty посчитанную position sizing-ом можно применить если торговля разрешена в IsAllowed.IsTradingAllowed
            const inLots = !isNullOrUndefined(orderEdit.sessionSettings) ? WDSettingsUtils.displayAmountInLots() : null;
            const minLot = Quantity.convertQuantityValue(new Quantity(ins.getMinLot(pt, acc), true), ins, inLots, acc, pt);
            const maxLot = Quantity.convertQuantityValue(new Quantity(ins.getMaxLot(pt, acc), true), ins, inLots, acc, pt);
            const tradingAllowed = qtyValue >= minLot && qtyValue <= maxLot;
            const reasonLocKey = tradingAllowed
                ? ''
                : 'positionSizeCalculator.OEButtonDisabled.' + (qtyValue < minLot ? 'less' : 'more');
            let reasonText = Resources.getResource(reasonLocKey);
            if (!tradingAllowed) {
                reasonText = reasonText.replace('{0}', (qtyValue < minLot ? minLot : maxLot).toString());
            }
            void this.set({
                tradingAllowed,
                tradingForbiddenReason: reasonText
            });
        }
        if (!isNullOrUndefined(OEQty)) {
            void OEQty.set('value', qtyValue);
            OEQty.updateQuantity(); /// // TODO !!!!! TODO !!!!!!!!!!!!!!!!!!!!!!!!!
        }
    }
    onDataSourceOpen() {
        this.deferLayout();
        const marginOE = this.Controls.marginOEControl;
        if (!isNullOrUndefined(marginOE)) {
            setTimeout(() => {
                marginOE.UpdateTable();
            }, 100);
        }
    }
}
RiskCalculator.TIME_TO_HIDE = 1000;
OrderEditViewBase.extendWith(RiskCalculator, {
    partials: {
        bodyPartial: RiskCalculatorTemplate,
        orderEditControls: OrderEditControlsTemplate
    },
    data: function () {
        return {
            showFooter: false,
            resizable: false,
            minWidth: 570,
            dockablePanel: false,
            showHeader: true,
            isAccountLinkShow: true,
            isSymbolLinkShow: true,
            instrument: null,
            account: null,
            quantity: null, // QuantityObject
            side: null,
            tif: null, // TIF object
            orderType: null,
            productType: null,
            productTypeShow: false,
            singleAccount: false,
            canFilterByAccount: false,
            selOrderTypeCBItem: null,
            isAlert: false,
            marginOEAllowed: false,
            marginOEVisible: false
        };
    }
});
